import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Link } from 'react-router-dom';
import { SiteHeader, SiteFooter } from './Layout';
import { AuthContext } from './AuthContext';
import { authFetch, startAuthFlow } from './auth';
import {
  Pencil, Trash2, Check, X, Plus, ArrowLeft, Save, RefreshCw,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// 절세 계좌(시뮬레이터에서 편집)와 OCR로 등록되는 ETF를 구분한다.
const ACCOUNT_IDS = ['isa', 'pension', 'irp'];
const isEtf = (p) => !ACCOUNT_IDS.includes(p.id);

// 수익률 기간 정의 (키, 라벨)
const RETURN_PERIODS = [
  ['1m', '1개월'], ['3m', '3개월'], ['6m', '6개월'], ['1y', '1년'],
];

// ── 포맷터 ───────────────────────────────────────────────────────────
const hasVal = (v) => v !== undefined && v !== null && v !== '';
const toNum = (v) => {
  if (!hasVal(v)) return undefined;
  const n = parseFloat(String(v).replace(/,/g, ''));
  return Number.isFinite(n) ? n : undefined;
};
const fmtWon = (n) => hasVal(n) ? Number(n).toLocaleString('ko-KR') + '원' : '—';
const fmtAum = (n) => hasVal(n) ? Number(n).toLocaleString('ko-KR') + '억' : '—';
const fmtPct = (n) => hasVal(n) ? `${Number(n) > 0 ? '+' : ''}${n}%` : '—';
const pctColor = (n) => !hasVal(n) ? 'text-white/30'
  : Number(n) > 0 ? 'text-green-400' : Number(n) < 0 ? 'text-red-400' : 'text-white/60';

// ── draft 변환 ───────────────────────────────────────────────────────
const emptyDraft = () => ({
  name: '', ticker: '', type: 'korea', isMonthly: false, tags: '',
  price: '', nav: '', aum: '', r1m: '', r3m: '', r6m: '', r1y: '',
});

const toDraft = (p) => {
  const r = p.returns || {};
  return {
    name: p.name || '',
    ticker: p.ticker || '',
    type: p.type === 'usa' ? 'usa' : 'korea',
    isMonthly: !!p.isMonthly,
    tags: Array.isArray(p.tags) ? p.tags.join(', ') : '',
    price: hasVal(p.price) ? String(p.price) : '',
    nav: hasVal(p.nav) ? String(p.nav) : '',
    aum: hasVal(p.aum) ? String(p.aum) : '',
    r1m: hasVal(r['1m']) ? String(r['1m']) : '',
    r3m: hasVal(r['3m']) ? String(r['3m']) : '',
    r6m: hasVal(r['6m']) ? String(r['6m']) : '',
    r1y: hasVal(r['1y']) ? String(r['1y']) : '',
  };
};

const fromDraft = (draft, id) => {
  const out = {
    id,
    name: draft.name.trim(),
    ticker: draft.ticker.trim(),
    type: draft.type === 'usa' ? 'usa' : 'korea',
    isMonthly: !!draft.isMonthly,
    tags: draft.tags.split(',').map(t => t.trim()).filter(Boolean),
  };
  // 숫자 필드는 값이 있을 때만 포함해 데이터를 깔끔하게 유지
  const price = toNum(draft.price); if (price !== undefined) out.price = price;
  const nav = toNum(draft.nav);     if (nav !== undefined) out.nav = nav;
  const aum = toNum(draft.aum);     if (aum !== undefined) out.aum = aum;
  const returns = {};
  for (const [k, dk] of [['1m', 'r1m'], ['3m', 'r3m'], ['6m', 'r6m'], ['1y', 'r1y']]) {
    const v = toNum(draft[dk]);
    if (v !== undefined) returns[k] = v;
  }
  if (Object.keys(returns).length) out.returns = returns;
  return out;
};

const genId = (draft) => {
  const prefix = draft.type === 'usa' ? 'US_ETF' : 'KOR_ETF';
  const suffix = draft.ticker.trim() || Date.now().toString(36).toUpperCase();
  return `${prefix}_${suffix}`;
};

export default function ManageProducts() {
  const { isSuwannews, loading: authLoading } = useContext(AuthContext);

  const [products, setProducts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  const [editingId, setEditingId] = useState(null); // 수정 중 id ('__new__' = 추가)
  const [draft, setDraft]         = useState(emptyDraft());
  const [saving, setSaving]       = useState(false);
  const [toast, setToast]         = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    fetch(`${BACKEND_URL}/api/products`)
      .then(r => { if (!r.ok) throw new Error('상품 데이터를 불러오지 못했습니다.'); return r.json(); })
      .then(data => setProducts(Array.isArray(data) ? data : []))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3500);
  };

  const persist = async (next) => {
    setProducts(next);
    setSaving(true);
    try {
      const res = await authFetch(`${BACKEND_URL}/api/products`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(next),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || `저장 실패 (HTTP ${res.status})`);
      }
      showToast('ok', '저장되었습니다.');
    } catch (e) {
      showToast('err', `저장 오류: ${e.message}`);
      load();
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (p) => { setEditingId(p.id); setDraft(toDraft(p)); };
  const startAdd  = () => { setEditingId('__new__'); setDraft(emptyDraft()); };
  const cancel    = () => { setEditingId(null); setDraft(emptyDraft()); };

  const saveRow = () => {
    if (!draft.name.trim() && !draft.ticker.trim()) {
      showToast('err', '종목명 또는 종목코드를 입력하세요.');
      return;
    }
    let next;
    if (editingId === '__new__') {
      const id = genId(draft);
      if (products.some(p => p.id === id)) {
        showToast('err', `이미 존재하는 종목코드입니다 (${id}).`);
        return;
      }
      next = [...products, fromDraft(draft, id)];
    } else {
      next = products.map(p => p.id === editingId ? fromDraft(draft, p.id) : p);
    }
    cancel();
    persist(next);
  };

  const deleteRow = (p) => {
    if (!window.confirm(`'${p.name || p.id}' 항목을 삭제할까요?`)) return;
    persist(products.filter(x => x.id !== p.id));
  };

  if (authLoading) {
    return <Shell><p className="text-center text-white/50 py-20">인증 확인 중...</p></Shell>;
  }
  if (!isSuwannews) {
    return (
      <Shell>
        <div className="max-w-md mx-auto text-center py-20">
          <h2 className="text-2xl font-bold mb-3">편집자 전용 페이지</h2>
          <p className="text-white/50 mb-8">데이터 관리는 편집자(suwannews) 계정만 접근할 수 있습니다.</p>
          <button onClick={startAuthFlow}
            className="px-8 py-3 bg-blue-600 rounded-xl font-bold hover:bg-blue-700 transition-colors">
            로그인
          </button>
          <div className="mt-6">
            <Link to="/simulator" className="text-white/40 hover:text-white/70 text-sm">← 시뮬레이터로 돌아가기</Link>
          </div>
        </div>
      </Shell>
    );
  }

  const etfs = products.filter(isEtf);

  return (
    <Shell>
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-black mb-1">등록 데이터 관리</h1>
          <p className="text-white/50 text-sm">등록된 ETF의 시세·수익률을 수정·삭제할 수 있습니다.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} disabled={saving}
            className="px-4 py-2.5 rounded-xl bg-white/10 border border-white/15 hover:bg-white/20 transition-colors flex items-center gap-2 text-sm font-semibold disabled:opacity-50">
            <RefreshCw className="w-4 h-4" /> 새로고침
          </button>
          <button onClick={startAdd} disabled={editingId !== null}
            className="px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 transition-colors flex items-center gap-2 text-sm font-bold disabled:opacity-50">
            <Plus className="w-4 h-4" /> ETF 추가
          </button>
        </div>
      </div>

      {toast && (
        <div className={`mb-6 px-4 py-3 rounded-xl text-sm font-semibold ${
          toast.type === 'ok' ? 'bg-green-500/15 text-green-300 border border-green-500/30'
                              : 'bg-red-500/15 text-red-300 border border-red-500/30'}`}>
          {toast.msg}
        </div>
      )}

      {loading ? (
        <p className="text-center text-white/50 py-20">불러오는 중...</p>
      ) : error ? (
        <p className="text-center text-red-400 py-20">오류: {error}</p>
      ) : (
        <div className="space-y-3">
          {editingId === '__new__' && (
            <EditCard draft={draft} setDraft={setDraft} onSave={saveRow} onCancel={cancel} saving={saving} isNew />
          )}

          {etfs.length === 0 && editingId !== '__new__' && (
            <div className="text-center text-white/40 py-16 bg-white/5 rounded-2xl border border-white/10">
              등록된 ETF가 없습니다. 'ETF 추가' 또는 시뮬레이터의 OCR 업로더로 등록하세요.
            </div>
          )}

          {etfs.map(p => (
            editingId === p.id
              ? <EditCard key={p.id} draft={draft} setDraft={setDraft} onSave={saveRow} onCancel={cancel} saving={saving} />
              : <ViewCard key={p.id} p={p} onEdit={() => startEdit(p)} onDelete={() => deleteRow(p)} disabled={editingId !== null} />
          ))}
        </div>
      )}

      <div className="mt-10">
        <Link to="/simulator" className="inline-flex items-center gap-2 text-white/40 hover:text-white/70 text-sm">
          <ArrowLeft className="w-4 h-4" /> 시뮬레이터로 돌아가기
        </Link>
      </div>
    </Shell>
  );
}

// ── 통계 한 칸 ───────────────────────────────────────────────────────
function Stat({ label, value, valueClass = 'text-white' }) {
  return (
    <div className="flex flex-col gap-0.5 min-w-[84px]">
      <span className="text-[11px] text-white/40">{label}</span>
      <span className={`text-sm font-semibold tabular-nums ${valueClass}`}>{value}</span>
    </div>
  );
}

// ── 보기 카드 ────────────────────────────────────────────────────────
function ViewCard({ p, onEdit, onDelete, disabled }) {
  const r = p.returns || {};
  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl px-5 py-4 hover:border-white/20 transition-colors">
      {/* 상단: 종목명/코드/배지/작업 */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-bold text-white text-lg">{p.name || <span className="text-white/30">(이름 없음)</span>}</span>
          <span className="text-white/50 font-mono text-sm">{p.ticker || '-'}</span>
          <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
            p.type === 'usa' ? 'bg-indigo-500/20 text-indigo-300' : 'bg-blue-500/20 text-blue-300'}`}>
            {p.type === 'usa' ? '미국' : '국내'}
          </span>
          {p.isMonthly && <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-500/20 text-green-300">월배당</span>}
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button onClick={onEdit} disabled={disabled} title="수정"
            className="p-2 rounded-lg bg-white/5 hover:bg-blue-500/20 hover:text-blue-300 text-white/50 transition-colors disabled:opacity-30">
            <Pencil className="w-4 h-4" />
          </button>
          <button onClick={onDelete} disabled={disabled} title="삭제"
            className="p-2 rounded-lg bg-white/5 hover:bg-red-500/20 hover:text-red-300 text-white/50 transition-colors disabled:opacity-30">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* 시세 */}
      <div className="flex flex-wrap gap-x-6 gap-y-2 mb-3">
        <Stat label="현재가" value={fmtWon(p.price)} />
        <Stat label="기준가" value={fmtWon(p.nav)} />
        <Stat label="순자산" value={fmtAum(p.aum)} />
      </div>

      {/* 수익률 */}
      <div className="flex flex-wrap gap-x-6 gap-y-2 pt-3 border-t border-white/10">
        {RETURN_PERIODS.map(([k, label]) => (
          <Stat key={k} label={`수익률 ${label}`} value={fmtPct(r[k])} valueClass={pctColor(r[k])} />
        ))}
      </div>

      {/* 태그 */}
      {(p.tags || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-3">
          {p.tags.map((t, i) => (
            <span key={i} className="px-2 py-0.5 rounded-md text-xs bg-white/10 text-white/60">{t}</span>
          ))}
        </div>
      )}
    </div>
  );
}

// ── 입력 한 칸 ───────────────────────────────────────────────────────
const inputCls = 'w-full bg-black/30 border border-white/15 rounded-lg px-3 py-2 text-sm text-white focus:border-blue-400 focus:outline-none';
function Field({ label, children }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] text-white/40">{label}</span>
      {children}
    </label>
  );
}

// ── 편집/추가 카드 ───────────────────────────────────────────────────
function EditCard({ draft, setDraft, onSave, onCancel, saving, isNew }) {
  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }));
  return (
    <div className={`rounded-2xl px-5 py-5 border ${isNew ? 'bg-blue-500/10 border-blue-500/30' : 'bg-white/10 border-white/25'}`}>
      {/* 기본 정보 */}
      <div className="grid grid-cols-2 md:grid-cols-[2fr_1fr_1fr_auto] gap-3 mb-4">
        <Field label="종목명">
          <input className={inputCls} placeholder="예: TIGER 미국배당다우존스" value={draft.name} onChange={e => set('name', e.target.value)} />
        </Field>
        <Field label="코드/티커">
          <input className={inputCls} placeholder="452320 / SCHD" value={draft.ticker} onChange={e => set('ticker', e.target.value)} />
        </Field>
        <Field label="구분">
          <select className={inputCls} value={draft.type} onChange={e => set('type', e.target.value)}>
            <option value="korea">국내</option>
            <option value="usa">미국</option>
          </select>
        </Field>
        <Field label="월배당">
          <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer select-none h-[38px]">
            <input type="checkbox" checked={draft.isMonthly} onChange={e => set('isMonthly', e.target.checked)} className="w-4 h-4 accent-blue-500" />
            월배당
          </label>
        </Field>
      </div>

      {/* 시세 */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <Field label="현재가 (원)"><input className={inputCls} inputMode="numeric" placeholder="10250" value={draft.price} onChange={e => set('price', e.target.value)} /></Field>
        <Field label="기준가 (원)"><input className={inputCls} inputMode="numeric" placeholder="10300" value={draft.nav} onChange={e => set('nav', e.target.value)} /></Field>
        <Field label="순자산 (억원)"><input className={inputCls} inputMode="numeric" placeholder="1200" value={draft.aum} onChange={e => set('aum', e.target.value)} /></Field>
      </div>

      {/* 수익률 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Field label="수익률 1개월 (%)"><input className={inputCls} inputMode="decimal" placeholder="2.1" value={draft.r1m} onChange={e => set('r1m', e.target.value)} /></Field>
        <Field label="수익률 3개월 (%)"><input className={inputCls} inputMode="decimal" placeholder="5.3" value={draft.r3m} onChange={e => set('r3m', e.target.value)} /></Field>
        <Field label="수익률 6개월 (%)"><input className={inputCls} inputMode="decimal" placeholder="8.0" value={draft.r6m} onChange={e => set('r6m', e.target.value)} /></Field>
        <Field label="수익률 1년 (%)"><input className={inputCls} inputMode="decimal" placeholder="12.4" value={draft.r1y} onChange={e => set('r1y', e.target.value)} /></Field>
      </div>

      {/* 태그 */}
      <div className="mb-5">
        <Field label="태그 (콤마로 구분)">
          <input className={inputCls} placeholder="미국배당, 월배당" value={draft.tags} onChange={e => set('tags', e.target.value)} />
        </Field>
      </div>

      <div className="flex gap-2">
        <button onClick={onSave} disabled={saving}
          className="px-6 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold transition-colors disabled:opacity-50 flex items-center gap-2">
          {isNew ? <Save className="w-4 h-4" /> : <Check className="w-4 h-4" />} 저장
        </button>
        <button onClick={onCancel} disabled={saving}
          className="px-6 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white/70 font-bold transition-colors disabled:opacity-50 flex items-center gap-2">
          <X className="w-4 h-4" /> 취소
        </button>
      </div>
    </div>
  );
}

// ── 공통 레이아웃 쉘 ─────────────────────────────────────────────────
function Shell({ children }) {
  return (
    <div className="min-h-screen text-white font-sans" style={{ background: '#060c1a' }}>
      <SiteHeader />
      <div className="relative max-w-6xl mx-auto px-4 md:px-8 pt-24 pb-16" style={{ zIndex: 10 }}>
        {children}
      </div>
      <SiteFooter />
    </div>
  );
}
